<script setup lang="ts">
import { computed, ref, useId, useTemplateRef, watch } from "vue"
import FormShell from "@/forms/components/FormShell.vue"
import { useForm } from "@/forms/composables/useForm"
import { notifySuccess, notifyErrors } from "../../notifications"
import MediaBatchEditItemsList from "./MediaBatchEditItemsList.vue"
import MediaBatchEditForm from "./MediaBatchEditForm.vue"
import { useBatchEditSession, type BatchResult } from "./useBatchEditSession"
import type { BatchEditItem } from "./batchEditItem"
import type { MediaUpdateInput } from "../../graphql/types"

const props = defineProps<{
  items: BatchEditItem[]
  save: (id: string, update: MediaUpdateInput) => Promise<void>
}>()

const emit = defineEmits<{
  "applied": [result: BatchResult]
  "close": []
}>()

// Wrap the plain-array prop in a ref so useBatchEditSession can snapshot it.
const itemsRef = computed(() => props.items)

const session = useBatchEditSession({ items: itemsRef, save: props.save })

const form = useForm<Record<string, unknown>>({
  initial: ref({}),
  save: async () => {}
})

const applying = ref(false)
const lastResult = ref<BatchResult | null>(null)

const applyDisabled = computed(() =>
  session.touchedCount.value === 0 || session.items.value.length === 0 || applying.value
)

const totalAttempted = computed(() =>
  (lastResult.value?.succeeded.length ?? 0) + (lastResult.value?.failed.length ?? 0)
)

async function onApply(): Promise<void> {
  applying.value = true
  try {
    const result = await session.applyToAll()
    lastResult.value = result
    if (result.failed.length === 0) {
      notifySuccess({ text: `${result.succeeded.length} Datei${result.succeeded.length === 1 ? '' : 'en'} aktualisiert` })
      emit("applied", result)
    } else {
      if (result.succeeded.length > 0) {
        notifySuccess({ text: `${result.succeeded.length} von ${totalAttempted.value} Dateien aktualisiert` })
      }
      notifyErrors(result.failed.map(f => new Error(`${f.filename}: ${f.error.message}`)))
    }
  } finally {
    applying.value = false
  }
}

const confirmOpen = ref(false)
const confirmTitleId = useId()
const confirmDialogRef = useTemplateRef<HTMLDialogElement>("confirmDialogRef")

watch(confirmOpen, open => {
  if (open && !confirmDialogRef.value?.open) confirmDialogRef.value?.showModal()
}, { flush: "post" })

const mobileItemsOpen = ref(false)

function onMobileItemsToggle(e: Event): void {
  mobileItemsOpen.value = (e.target as HTMLDetailsElement).open
}

function requestClose(): void {
  if (session.touchedCount.value > 0) {
    confirmOpen.value = true
  } else {
    emit("close")
  }
}

function onConfirmDiscard(): void {
  confirmOpen.value = false
  emit("close")
}

function onConfirmCancel(): void {
  confirmOpen.value = false
}

defineExpose({ requestClose })
</script>

<template>
  <div class="grid md:grid-cols-[240px_minmax(0,1fr)] h-full min-h-0">
    <aside class="border-r border-base-300 bg-base-200 min-h-0 md:block hidden">
      <MediaBatchEditItemsList
        :items="session.items.value"
        :failed="session.failed.value"
        :preview-filename="session.previewFilename"
        @remove="session.removeItem"
      />
    </aside>
    <div class="flex flex-col min-h-0">
      <!-- Mobile-only collapsible items header (< md) -->
      <details
        class="md:hidden border-b border-base-300 bg-base-200 group"
        @toggle="onMobileItemsToggle"
      >
        <summary class="cursor-pointer select-none px-5 py-3 text-sm flex items-center gap-2">
          <span class="transition-transform group-open:rotate-90">▸</span>
          <span>{{ session.items.value.length }} Datei{{ session.items.value.length === 1 ? '' : 'en' }}</span>
        </summary>
        <div
          v-if="mobileItemsOpen"
          class="max-h-64 overflow-y-auto border-t border-base-300 bg-base-100"
        >
          <MediaBatchEditItemsList
            :items="session.items.value"
            :failed="session.failed.value"
            :preview-filename="session.previewFilename"
            @remove="session.removeItem"
          />
        </div>
      </details>
      <header class="px-5 py-4 border-b border-base-300 flex items-start justify-between">
        <div>
          <h2 class="font-semibold text-base">Dateien bearbeiten</h2>
          <p class="text-xs text-base-content/60 mt-0.5">
            Nur geänderte Felder werden angewendet.
          </p>
        </div>
        <button
          data-testid="batch-close"
          type="button"
          class="btn btn-ghost btn-sm btn-circle"
          @click="requestClose"
        >✕</button>
      </header>

      <div
        v-if="lastResult && lastResult.failed.length > 0"
        data-testid="batch-partial-alert"
        class="alert alert-warning rounded-none text-sm"
      >
        {{ lastResult.succeeded.length }} von {{ totalAttempted }} Dateien aktualisiert —
        {{ lastResult.failed.length }} fehlgeschlagen. Siehe Markierungen links.
      </div>

      <section class="flex-1 overflow-y-auto p-5">
        <FormShell :form="form">
          <MediaBatchEditForm :session="session" />
        </FormShell>
      </section>

      <footer class="px-5 py-3 border-t border-base-300 bg-base-200 flex justify-between items-center">
        <div class="text-xs text-base-content/70">
          <span v-if="session.touchedCount.value > 0" class="badge badge-warning badge-sm">
            {{ session.touchedCount.value }} {{ session.touchedCount.value === 1 ? "Feld" : "Felder" }} geändert
          </span>
        </div>
        <div class="flex gap-2">
          <button
            data-testid="batch-cancel"
            type="button"
            class="btn btn-ghost btn-sm"
            @click="requestClose"
          >Abbrechen</button>
          <button
            data-testid="batch-apply"
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="applyDisabled"
            @click="onApply"
          >
            Auf {{ session.items.value.length }} Datei{{ session.items.value.length === 1 ? '' : 'en' }} anwenden
          </button>
        </div>
      </footer>
    </div>

    <dialog
      v-if="confirmOpen"
      ref="confirmDialogRef"
      class="modal"
      :aria-labelledby="confirmTitleId"
      data-testid="batch-confirm-discard"
      @cancel.prevent="onConfirmCancel"
    >
      <div class="modal-box">
        <h3 :id="confirmTitleId" class="font-bold text-lg">Änderungen verwerfen?</h3>
        <p class="py-4 text-sm">
          Du hast {{ session.touchedCount.value }} Feld{{ session.touchedCount.value === 1 ? '' : 'er' }}
          geändert. Wenn du jetzt abbrichst, gehen diese verloren.
        </p>
        <div class="modal-action">
          <button
            type="button"
            class="btn btn-ghost"
            @click="onConfirmCancel"
          >Weiter bearbeiten</button>
          <button
            data-testid="batch-confirm-discard-confirm"
            type="button"
            class="btn btn-error"
            @click="onConfirmDiscard"
          >Verwerfen</button>
        </div>
      </div>
    </dialog>
  </div>
</template>
